import { LDateTime } from '@app/components/date/LDateTime';
import { StationLink } from '@app/components/stations/StationLink';
import { ListViewLocalChildProps } from '@app/core/list-view/ListViewLocal';
import { FragmentLink } from '@app/modules/fragments/components/FragmentLink';
import {
  FragmentSourceDto,
  UploadDto,
  UploadDtoStatusEnum,
} from '@app/modules/social-export/social-export.types';
import { DateTimeStr } from '@mi-front/core';
import { MrtTable } from '@mi-front/ui';
import { Box, Chip } from '@mui/material';
import { useIntl } from 'react-intl';

export type PlatformUploadedTableProps = ListViewLocalChildProps<UploadDto>;

/**
 * Social-export uploaded videos table for ListViewLocal. Filters live in the DataToolbar.
 */
export function PlatformUploadedTable({
  data,
  isLoading = false,
  isFetching = false,
  showProgress = true,
}: PlatformUploadedTableProps) {
  const { $t } = useIntl();

  return (
    <Box>
      <MrtTable<UploadDto>
        data={data}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableToolbarInternalActions={false}
        state={{
          isLoading,
          showProgressBars: showProgress && (isLoading || isFetching),
        }}
        muiTopToolbarProps={{
          sx: {
            height: '4px !important',
            minHeight: '4px !important',
          },
        }}
        columns={[
          {
            header: $t({
              id: 'socialExport.uploaded.columnStation',
              defaultMessage: 'Estación',
            }),
            accessorKey: 'stationId',
            Cell: ({ row }) =>
              row.original.stationId && (
                <StationLink
                  id={row.original.stationId}
                  link={`/fragments/s/${row.original.stationId}`}
                />
              ),
          },
          {
            header: $t({
              id: 'socialExport.uploaded.columnFragment',
              defaultMessage: 'Fragmento',
            }),
            accessorKey: 'fragmentId',
            Cell: ({ row }) => {
              const source = row.original.source as FragmentSourceDto;
              return <FragmentLink id={source.data.fragmentId} />;
            },
          },
          {
            header: $t({
              id: 'socialExport.uploaded.columnStatus',
              defaultMessage: 'Estado',
            }),
            accessorKey: 'status',
            Cell: ({ row }) => (
              <Chip
                color={
                  row.original.status === UploadDtoStatusEnum.COMPLETED
                    ? 'success'
                    : row.original.status === UploadDtoStatusEnum.IN_PROGRESS
                      ? 'info'
                      : row.original.status === UploadDtoStatusEnum.SCHEDULED
                        ? 'warning'
                        : 'error'
                }
                label={row.original.status}
              />
            ),
          },
          {
            header: $t({
              id: 'socialExport.uploaded.columnPublished',
              defaultMessage: 'Publicado',
            }),
            accessorKey: 'createdAt',
            Cell: ({ row }) => (
              <LDateTime value={row.original.createdAt as DateTimeStr} showSeconds />
            ),
          },
        ]}
      />
    </Box>
  );
}
